/**
 * Custom Hooks for Careers (Job Postings)
 * Provides React hooks for managing career/job posting data
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { careersService } from '../services';
import { Career } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all careers
 */
export function useCareers(
  options?: ResourceQueryOptions
): ResourceState<Career> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      careersService.getAll(opts) as unknown as Promise<{ data: Career[]; meta?: any }>,
    []
  );
  return useResourceCollection<Career>(fetchFn, options);
}

/**
 * Hook to fetch a single career by ID
 */
export function useCareer(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<Career>((id) => careersService.getById(id, options), id);
}

/**
 * Hook to create a new career
 */
export function useCreateCareer(onSuccess?: () => void): MutationState<Career> {
  return useCreateResource<Career>(careersService.create, onSuccess);
}

/**
 * Hook to update an existing career
 */
export function useUpdateCareer(onSuccess?: () => void): MutationState<Career> {
  return useUpdateResource<Career>(careersService.update, onSuccess);
}

/**
 * Hook to delete a career
 */
export function useDeleteCareer(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(careersService.delete, onSuccess);
}

